import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from '../../../lib/firebase/authReadyCallable';
import { useApiLoadingStatus } from '@vis.gl/react-google-maps';
import { db, functions } from '../../../config/firebase';
import { COLLECTIONS } from '../../../config/collections';
import type { Address } from '../../../types/shared';
import type { GeocodeResult } from './types';
import { resolveCoords, resolveAddressString, normalizeAddressForDocId } from './utils';
import { useMapApiContext } from './MapOverlayProvider';
import { logError } from '../../../lib/datahub/errorLogging/captureError';
import { waitForGoogleMapsReady, describeMapsUnavailable } from './mapsReadiness';
import { useCanSaveGeocode } from './useCanSaveGeocode';

/**
 * True when the Geocoder is telling us the address does not exist, rather than
 * that something is broken.
 *
 * `Geocoder.geocode()` REJECTS on ZERO_RESULTS rather than resolving with an
 * empty list, so the not-found answer and a genuine fault arrive down the same
 * path and can only be told apart by the status string. Matched on the message
 * because that is what the SDK puts there; a status property is checked first
 * where the SDK provides one.
 *
 * Failure direction is deliberate: anything this does not recognise is treated
 * as a FAULT and reported. An over-broad match here would re-create the silent
 * hole this whole change closes.
 */
export function isAddressNotFound(err: unknown): boolean {
  const status = (err as { code?: unknown })?.code;
  if (typeof status === 'string' && status === 'ZERO_RESULTS') return true;
  return err instanceof Error && err.message.includes('ZERO_RESULTS');
}

export function useGeocode(
  address: string | Address | null,
  lat?: number,
  lng?: number,
): GeocodeResult {
  const { requestMapApi } = useMapApiContext();
  const [result, setResult] = useState<GeocodeResult>({
    lat: null,
    lng: null,
    isLoading: false,
    error: null,
  });

  // `waitForGoogleMapsReady` polls from inside an async closure, where a
  // hook's return value would be stale for the lifetime of that closure.
  // Mirror the live status into a ref so the poll always reads the current
  // value.
  const apiLoadingStatus = useApiLoadingStatus();
  const apiLoadingStatusRef = useRef(apiLoadingStatus);
  useEffect(() => {
    apiLoadingStatusRef.current = apiLoadingStatus;
  }, [apiLoadingStatus]);

  // Same ref pattern as above: the entitlement can change (the profile loads after first render)
  // and must not re-run the geocode, so `resolve` reads it at save time instead of closing over it.
  const canSaveGeocode = useCanSaveGeocode();
  const canSaveGeocodeRef = useRef(canSaveGeocode);
  useEffect(() => {
    canSaveGeocodeRef.current = canSaveGeocode;
  }, [canSaveGeocode]);

  const resolve = useCallback(async (signal?: AbortSignal) => {
    if (!address) {
      setResult({ lat: null, lng: null, isLoading: false, error: 'No address provided' });
      return;
    }

    const coords = resolveCoords(address, lat, lng);
    if (coords) {
      setResult({ lat: coords.lat, lng: coords.lng, isLoading: false, error: null });
      return;
    }

    const addressStr = resolveAddressString(address);
    if (!addressStr) {
      setResult({ lat: null, lng: null, isLoading: false, error: 'Empty address' });
      return;
    }

    // Ensure the Maps SDK is requested before we attempt to use the Geocoder.
    requestMapApi();

    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const docId = normalizeAddressForDocId(addressStr);
      const cacheRef = doc(db, COLLECTIONS.GEOCODE_CACHE, docId);
      const cacheSnap = await getDoc(cacheRef);

      if (cacheSnap.exists()) {
        const cached = cacheSnap.data();
        setResult({ lat: cached.lat, lng: cached.lng, isLoading: false, error: null });
        return;
      }

      // Wait for the Maps SDK to actually be ready instead of racing it:
      // `requestMapApi()` above only flips a React flag — `<APIProvider>`
      // mounts on the NEXT render and injects the Maps JS script
      // asynchronously after that, so `window.google` is reliably still
      // absent on this very first pass. That is expected, not a fault.
      if (!window.google?.maps?.Geocoder) {
        const outcome = await waitForGoogleMapsReady(() => apiLoadingStatusRef.current, undefined, signal);
        if (outcome !== 'READY') {
          const message = describeMapsUnavailable(outcome);
          // OFFLINE is excluded from reporting for the same reason
          // ZERO_RESULTS is below: it is an expected condition on the
          // user's end, not a fault in our code or Google's.
          if (outcome !== 'OFFLINE') {
            logError(new Error(`Maps SDK not ready: ${outcome}`), {
              severity: 'error',
              componentName: 'useGeocode',
              additionalContext: {
                mapsFailure: 'sdk_load',
                outcome,
                apiLoadingStatus: apiLoadingStatusRef.current,
              },
            });
          }
          setResult({ lat: null, lng: null, isLoading: false, error: message });
          return;
        }
      }

      // Defensive, not expected: `waitForGoogleMapsReady` resolving READY
      // means it observed `window.google.maps.Geocoder` directly, but that
      // observation can't survive as a TS type-narrowing across the `await`
      // above. Re-check explicitly rather than asserting non-null.
      const Geocoder = window.google?.maps?.Geocoder;
      if (!Geocoder) {
        const message = describeMapsUnavailable('FAILED');
        logError(new Error('Maps SDK reported ready but window.google.maps.Geocoder is missing'), {
          severity: 'error',
          componentName: 'useGeocode',
          additionalContext: { mapsFailure: 'sdk_load', outcome: 'INCONSISTENT_READY' },
        });
        setResult({ lat: null, lng: null, isLoading: false, error: message });
        return;
      }

      const geocoder = new Geocoder();
      const response = await geocoder.geocode({ address: addressStr });

      if (response.results.length === 0) {
        setResult({ lat: null, lng: null, isLoading: false, error: 'Address not found' });
        return;
      }

      const location = response.results[0].geometry.location;
      const geocodedLat = location.lat();
      const geocodedLng = location.lng();

      // The server only stores a result for P6 users and answers everyone else with a 403, so do not ask.
      if (canSaveGeocodeRef.current) {
        const saveGeocodeFn = httpsCallable(functions, 'shared_saveGeocodeResult');
        saveGeocodeFn({ docId: docId, data: { address: addressStr, lat: geocodedLat, lng: geocodedLng } }).catch(() => {});
      }

      setResult({ lat: geocodedLat, lng: geocodedLng, isLoading: false, error: null });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Geocoding failed';
      // The user-facing behaviour is unchanged — the overlay still shows the
      // message in its skeleton. What is new is that we also REPORT it.
      //
      // This catch was the largest silent hole in the map feature: a revoked
      // API key, an exhausted quota, a REQUEST_DENIED from a referrer
      // restriction, or a Firestore refusal on the geocode cache all landed
      // here and went no further than a grey panel. Reporting from OUR catch
      // block is the only way any of them is ever seen with a real stack —
      // once the same fault surfaces through `window.onerror` instead, the
      // browser has already redacted it to "Script error.".
      //
      // ZERO_RESULTS is excluded because it is an ANSWER, not a fault: the
      // address simply is not on the map, which is a data-quality fact the
      // overlay already shows the user and which would otherwise become the
      // single noisiest group in the store.
      if (!isAddressNotFound(err)) {
        logError(err, {
          severity: 'error',
          componentName: 'useGeocode',
          additionalContext: { mapsFailure: 'geocode', geocodeMessage: message },
        });
      }
      setResult({ lat: null, lng: null, isLoading: false, error: message });
    }
  }, [address, lat, lng, requestMapApi]);

  useEffect(() => {
    // The SDK-readiness wait inside `resolve` is the one part of this effect
    // that can run long (up to MAPS_LOAD_TIMEOUT_MS). If this component
    // unmounts (or `resolve` changes again because `address`/`lat`/`lng`
    // changed) while that wait is still pending, aborting it stops the poll
    // outright rather than letting it run to completion and call
    // `setResult`/`logError` for a request nobody cares about anymore.
    const controller = new AbortController();
    resolve(controller.signal);
    return () => controller.abort();
  }, [resolve]);

  return result;
}
